import unionWith from 'lodash/unionWith';
import { filter, omit, path, split } from 'ramda';

import config from '../../config';
import { storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { parse } from '../../util/urlHelpers';
import { getSearchLocation, getSortSearchParams, typeOfPoolSearchParams } from '../../util/search';
import { klaviyoUpdateSearchCity } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import {
  getBookingDateSearchParams,
  getParticipantsSearchParams,
} from './SearchPage.helpers';
import { isString } from 'lodash';
import { isNilOrEmpty } from '../../util/validators';
import { refreshCartMaybe } from '../../ducks/user.duck';
import { getHours, moment } from '../../util/dates';

const MAX_SIMILAR_LISTINGS = 3;
const RESULT_PAGE_SIZE = 24;
const RESULT_PAGE_SIZE_WITH_DATE = 100;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const SEARCH_SIMILAR_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_SIMILAR_LISTINGS_SUCCESS';
export const SET_SIMILAR_LISTINGS = 'app/SearchPage/SET_SIMILAR_LISTINGS';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  searchMapListingIds: [],
  searchMapListingsError: null,
  similarListingIds: [],
};

const resultIds = data => data.data.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_LISTINGS_REQUEST:
      return {
        ...state,
        searchMapListingsError: null,
      };
    case SEARCH_MAP_LISTINGS_SUCCESS: {
      const searchMapListingIds = unionWith(
        state.searchMapListingIds,
        resultIds(payload.data),
        (id1, id2) => id1.uuid === id2.uuid,
      );
      return {
        ...state,
        searchMapListingIds,
      };
    }
    case SEARCH_MAP_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchMapListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case SEARCH_SIMILAR_LISTINGS_SUCCESS: {
      const resultsIdsWithoutCurrentListing = filter(id => id.uuid !== payload.listingId.uuid, resultIds(payload.data));
      const sortedResultIds = resultsIdsWithoutCurrentListing.sort(() => 0.5 - Math.random());
      const similarListingIds = sortedResultIds.slice(0, MAX_SIMILAR_LISTINGS);
      return {
        ...state,
        similarListingIds: similarListingIds,
        searchInProgress: false,
      };
    }
    case SET_SIMILAR_LISTINGS: {
      const resultsIdsWithoutCurrentListing = filter(id => id.uuid !== payload.listingId.uuid, payload.data);
      const sortedResultIds = resultsIdsWithoutCurrentListing.sort(() => 0.5 - Math.random());
      const similarListingIds = sortedResultIds.slice(0, MAX_SIMILAR_LISTINGS);
      return {
        ...state,
        similarListingIds: similarListingIds,
      };
    }

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchMapListingsRequest = () => ({ type: SEARCH_MAP_LISTINGS_REQUEST });

export const searchMapListingsSuccess = response => ({
  type: SEARCH_MAP_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchMapListingsError = e => ({
  type: SEARCH_MAP_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchSimilarListingsSuccess = (response, listingId) => ({
  type: SEARCH_SIMILAR_LISTINGS_SUCCESS,
  payload: { data: response.data, listingId: listingId },
});

export const setSimilarListings = (response, listingId) => ({
  type: SET_SIMILAR_LISTINGS,
  payload: { data: response, listingId: listingId },
});

const trackSearch = (email, city, isEvent) => {
  try {
    if (!email || !city) return;

    window.sendinblue.track(
      isEvent ? 'Event Search' : 'Pool Search',
      {
        email,
        SEARCH_CITY: city,
      },
      {
        SEARCH_CITY: city,
      },
    );
  } catch (e) {
    console.log('trackSearch err', e);
  }
};

export const searchListings = (searchParams, search) => (dispatch, getState, sdk) => {
  const searchParamsWithOriginMaybe = config.sortSearchByDistance ? searchParams : omit(['origin'], searchParams);
  dispatch(searchListingsRequest(searchParamsWithOriginMaybe));
  const priceSearchParams = priceParam => {
    const inSubunits = value =>
      convertUnitToSubUnit(value, unitDivisor(config.currencyConfig.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
        price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
      }
      : {};
  };

  const {
    adults,
    children = 0,
    endTime,
    isFlexible,
    infants = 0,
    perPage,
    price,
    bookingDate,
    startTime,
    typeOfPool,
    origin,
    type,
    _sort,
    ...rest
  } = searchParamsWithOriginMaybe;
  const { address, bounds, city } = rest;
  const priceMaybe = priceSearchParams(price);
  const participantsMaybe = getParticipantsSearchParams(adults, children, infants);
  const typeOfPoolMaybe = typeOfPoolSearchParams(typeOfPool);
  const sortMaybe = getSortSearchParams(_sort, origin);
  const bookingDateMaybe = getBookingDateSearchParams(bookingDate, startTime, endTime, isFlexible);
  const instantMaybe = type === 'instant' ? {
    pub_instantBookingFrom: '1,',
  } : {};
  const nowDate = new Date().getTime();
  console.log('nowDate', nowDate, 1712070852439)

  const params = {
    ...rest,
    ...priceMaybe,
    ...instantMaybe,
    ...bookingDateMaybe,
    ...participantsMaybe,
    ...typeOfPoolMaybe,
    ...sortMaybe,
    per_page: perPage
  };
  const boostedParams = {
    ...rest,
    ...priceMaybe,
    ...instantMaybe,
    ...bookingDateMaybe,
    ...participantsMaybe,
    ...typeOfPoolMaybe,
    meta_boostedScore: '1,',
    meta_boostedLimitAt: `${nowDate},`,
    sort: 'meta_boostedScore,meta_score',
    per_page: 100,
  };
  const searchLocation = getSearchLocation(searchParams);
  const numberOfPeople = !isNilOrEmpty(participantsMaybe) && {
    numberOfAdults: adults,
    numberOfChildren: children,
    numberOfInfants: infants,
  };
  const getBookingSearchId = (numberOfPeople, start, end) =>
    `${JSON.stringify(searchLocation)}_${start}_${end}_${JSON.stringify(numberOfPeople)}`;
  const bookingSearchId = (!isNilOrEmpty(bookingDateMaybe) && !isNilOrEmpty(participantsMaybe)) ?
    getBookingSearchId(numberOfPeople, bookingDateMaybe.start, bookingDateMaybe.end) : '';
  const getBookingParams = (bookingData) => {
    if (!bookingData) return {};

    const start = moment(bookingDateMaybe.start);
    const end = moment(bookingDateMaybe.end);
    return {
      startAt: start.toISOString(),
      endAt: end.toISOString(),
      nbHoursSelected: Math.abs(getHours(end) - getHours(start)),
      numberOfPeople,
    };
  };
  console.log('bookingSearchId', searchParams, bookingSearchId);
  dispatch(refreshCartMaybe(bookingSearchId, {
    bookingSearchId,
    bookingParams: getBookingParams(bookingDateMaybe),
  }));

  const userEmail = path(['user', 'currentUser', 'attributes', 'email'], getState());
  if (searchParams.page === 1) {
    trackSearch(userEmail, city, !!searchParams.event);
  }
  if (userEmail && address && bounds && city && origin) {
    const searchParamsStr = `%3Faddress=${address}%26bounds=${bounds.ne.lat},${bounds.ne.lng},${bounds.sw.lat},${bounds.sw.lng}%26city=${city}%26origin=${origin.lat},${origin.lng}`;
    klaviyoUpdateSearchCity({
      email: userEmail,
      searchCity: city,
      searchParams: searchParamsStr,
    });
  }

  const isFirstPage = searchParams.page === 1;
  const shouldFetchBoostedListingsSeparately = isFirstPage && params.origin;

  const boostedPromise = shouldFetchBoostedListingsSeparately
    ? sdk.listings.query(boostedParams).catch(e => {
      dispatch(searchListingsError(storableError(e)));
      return { data: { data: [], included: [] } };
    })
    : Promise.resolve({ data: { data: [], included: [] } });

  const normalPromise = sdk.listings.query(params).catch(e => {
    dispatch(searchListingsError(storableError(e)));
    return { data: { data: [], included: [] } };
  });

  return Promise.all([boostedPromise, normalPromise])
    .then(([boostedResponse, normalResponse]) => {
      const boostedListings = boostedResponse.data.data;
      const normalListings = normalResponse.data.data;

      const boostedIds = new Set(boostedListings.map(l => l.id.uuid));
      const mergedListings = [
        ...boostedListings,
        ...normalListings.filter(l => !boostedIds.has(l.id.uuid))
      ];

      const includedMap = new Map();
      const mergeIncluded = (items) => {
        (items || []).forEach(item => includedMap.set(item.id.uuid, item));
      };
      mergeIncluded(boostedResponse.data.included);
      mergeIncluded(normalResponse.data.included);

      const mergedIncluded = Array.from(includedMap.values());

      const mergedResponse = {
        status: normalResponse.status,
        statusText: normalResponse.statusText,
        data: {
          data: mergedListings,
          included: mergedIncluded,
          meta: {
            ...normalResponse.data.meta,
          }
        }
      };

      dispatch(addMarketplaceEntities(boostedResponse));
      dispatch(addMarketplaceEntities(normalResponse));
      dispatch(searchListingsSuccess(mergedResponse));

      return mergedResponse;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e)));
      throw e;
    });


};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const searchMapListings = searchParams => (dispatch, getState, sdk) => {
  dispatch(searchMapListingsRequest(searchParams));

  const { perPage, ...rest } = searchParams;
  const params = {
    ...rest,
    sort: 'meta_score',
    per_page: perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(searchMapListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(searchMapListingsError(storableError(e)));
      throw e;
    });
};

export const searchSimilarListings = listingId => async (dispatch, getState, sdk) => {
  const state = getState();
  const { currentPageResultIds, searchParams } = state.SearchPage;
  if (searchParams && searchParams.city) {
    return dispatch(setSimilarListings(currentPageResultIds, listingId));
  } else {
    return null;
  }
};

export const loadData = (params, search) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const { page = 1, origin, bookingDate, ...rest } = queryParams;
  const originMaybe = config.sortSearchByDistance && origin ? { origin } : {};

  return searchListings({
    ...rest,
    ...originMaybe,
    ...bookingDate && { bookingDate },
    page,
    perPage: bookingDate ? RESULT_PAGE_SIZE_WITH_DATE : RESULT_PAGE_SIZE,
    include: ['author', 'images'],
    'fields.listing': ['title', 'geolocation', 'price', 'publicData', 'createdAt', 'metadata'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
  }, search);
};
